import React, {
  useState,
  useImperativeHandle,
  forwardRef,
  useRef,
} from "react";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import SkuflixCrossIcon from "core/icons/skuflix/SkuflixCrossIcon";
import SkuflixSendIcon from "core/icons/skuflix/SkuflixSendIcon";
import SkuflixPeopleIcon from "core/icons/skuflix/SkuflixPeopleIcon";
import SkuflixVolumeUpIcon from "core/icons/skuflix/SkuflixVolumeUpIcon";
import SkuflixVolumeOffIcon from "core/icons/skuflix/SkuflixVolumeOffIcon";
import Badge from "@mui/material/Badge";
import { useTranslation } from "react-i18next";
import { useArena } from "core/components/ArenaProvider";
import { CreateChatMessageMutation } from "core/mutations";
import {
  BanCreatedSubscription,
  BanDeletedSubscription,
} from "core/subscriptions";
import { useMutation, useSubscription, gql } from "@apollo/client";
import { withSnackbar } from "notistack";
import ChatOnlineList from "core/components/ChatOnlineList";
import ChatSoundSettings from "core/components/ChatSoundSettings";

const ChatMessageFragment = gql`
  fragment ChatMessageFragment on ChatMessage {
    id
    messageType
    userId
    userDisplayName
    userAvatar
    userIsRightholder
    userIsAdmin
    message
    createdAt
    systemEventType
    systemEventData
    mentionUserId
    mentionUser {
      id
      fullName
    }
    replyToMessageId
    replyToMessage {
      id
      userId
      userDisplayName
      message
    }
    sponsorChatMessage {
      id
      token
      text
      link
      isClickable
      impressionPixel
      clickPixel
      thumbnails {
        format
        url
      }
    }
  }
`;

const StyledAppBar = styled(AppBar)({
  zIndex: 2,
});

const ENTER_KEY = 13;
const MAX_MESSAGE_LENGTH = 200;
const CHAT = "CHAT";

const ChatHeaderWithForm = forwardRef(function ChatHeaderWithForm(
  {
    tab,
    chatRoom,
    rightholderId,
    enqueueSnackbar,
    viewer,
    onOpenSignIn,
    replyTo,
    onCancelReply,
    mention,
    onClearMention,
    onlineCount,
    onlineMembers,
    chatRoomId,
    onMention,
    soundSettings,
    onUpdateSoundSettings,
    onInputFocus,
    onInputBlur,
  },
  ref
) {
  const { t } = useTranslation("core");
  const arena = useArena();
  const [onlineListOpen, setOnlineListOpen] = useState(false);
  const [soundAnchorEl, setSoundAnchorEl] = useState(null);

  const [message, setMessage] = useState("");
  const inputRef = useRef(null);

  useImperativeHandle(ref, () => ({
    insertMention(displayName) {
      setMessage((prev) => {
        const mentionText = `@${displayName}`;
        if (prev.includes(mentionText)) {
          return prev;
        }
        return prev + mentionText + " ";
      });
      if (inputRef.current) inputRef.current.focus();
    },
    focus() {
      if (inputRef.current) inputRef.current.focus();
    },
  }));

  const onError = (error) => {
    enqueueSnackbar(error.message, {
      variant: "error",
    });
  };

  const [createChatMessage, { loading }] = useMutation(
    CreateChatMessageMutation,
    {
      onError,
      optimisticResponse: {
        __typename: "Mutation",
        createChatMessage: {
          __typename: "ChatMessage",
          id: Date.now().toString(20),
          messageType: "USER",
          userId: viewer?.id,
          userDisplayName: viewer?.fullName,
          userAvatar:
            viewer?.photo?.url || viewer?.photoUrl || arena?.defaultAvatar?.url,
          userIsRightholder: chatRoom.viewerIsRightholder || false,
          userIsAdmin: viewer?.isAdmin,
          message,
          createdAt: new Date(),
          sponsorChatMessage: null,
          systemEventType: null,
          systemEventData: null,
          mentionUserId: mention?.userId || null,
          mentionUser: mention
            ? {
                __typename: "ArenaUser",
                id: mention.userId,
                fullName: mention.userDisplayName,
                userDisplayName: mention.userDisplayName,
              }
            : null,
          replyToMessageId: replyTo?.id || null,
          replyToMessage: replyTo
            ? {
                __typename: "ChatMessage",
                id: replyTo.id,
                userId: replyTo.userId || null,
                userDisplayName: replyTo.userDisplayName,
                message: replyTo.message,
              }
            : null,
        },
      },
      update: (cache, { data: { createChatMessage } }) => {
        if (!createChatMessage) return;

        cache.modify({
          fields: {
            chatMessages(existing, { storeFieldName }) {
              if (!storeFieldName.includes(chatRoom.id)) return existing;
              const newRef = cache.writeFragment({
                data: createChatMessage,
                fragment: ChatMessageFragment,
              });
              return {
                ...existing,
                count: (existing.count || 0) + 1,
                nodes: [newRef, ...(existing.nodes || [])],
              };
            },
          },
        });
      },
    }
  );

  const variables = viewer && {
    userId: viewer.id,
    rightholderId,
  };

  const banCreatedResult = useSubscription(BanCreatedSubscription, {
    skip: !process.browser || !viewer,
    variables,
  });

  const banDeletedResult = useSubscription(BanDeletedSubscription, {
    skip: !process.browser || !viewer,
    variables,
  });

  const onChange = (event) => {
    if (event.target.value.length <= MAX_MESSAGE_LENGTH) {
      setMessage(event.target.value);
    }
  };

  const signIn = () => {
    if (!viewer) {
      if (onOpenSignIn) {
        onOpenSignIn();
      } else {
        if (document.querySelector(".js-sign-in")) {
          return document.querySelector(".js-sign-in").click();
        }
      }
    }
  };

  const onClick = () => {
    signIn();
  };

  const createMessage = () => {
    signIn();

    if (message.trim()) {
      createChatMessage({
        variables: {
          input: {
            chatRoomId: chatRoom.id,
            message,
            replyToMessageId: replyTo?.id || undefined,
            mentionUserId: mention?.userId || undefined,
          },
        },
      });
      setMessage("");
      if (onCancelReply) onCancelReply();
      if (onClearMention) onClearMention();
      if (inputRef.current) inputRef.current.focus();
    }
  };

  const onKeyDown = (event) => {
    if (event.keyCode === ENTER_KEY && !event.shiftKey) {
      event.preventDefault();
      event.stopPropagation();
      createMessage();
    }
  };

  const onSubmit = (event) => {
    event.preventDefault();
    event.stopPropagation();
    createMessage();
  };

  const disabled = !message.trim() || loading;

  let viewerBan = chatRoom.viewerBan;

  if (banCreatedResult.data) {
    viewerBan = banCreatedResult.data.banCreated;
  }

  if (viewerBan) {
    if (banDeletedResult.data) {
      if (banDeletedResult.data.banDeleted.id === viewerBan.id) {
        viewerBan = null;
      }
    }
  }

  const soundEnabled =
    soundSettings?.replySound ||
    soundSettings?.mentionSound ||
    soundSettings?.joinSound;

  return (
    <StyledAppBar
      color="default"
      sx={{ backgroundColor: "background.body" }}
      position="static"
      component="form"
      onSubmit={onSubmit}
      onClick={onClick}
    >
      <Toolbar
        sx={{
          paddingLeft: "12px!important",
          paddingRight: "12px!important",
          gap: 1,
        }}
      >
        {viewerBan ? (
          <Typography variant="caption" sx={{ flex: 1 }}>
            {t("chat.banned", {
              date: viewerBan.expiredAt,
            })}
          </Typography>
        ) : (
          <TextField
            inputRef={inputRef}
            fullWidth
            multiline
            variant="outlined"
            size="small"
            minRows={1}
            maxRows={3}
            placeholder={t("chat.input")}
            value={message}
            onChange={onChange}
            onFocus={onInputFocus}
            onBlur={onInputBlur}
            InputProps={{
              onKeyDown,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton type="submit" edge="end" disabled={disabled}>
                    <SkuflixSendIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              flex: 1,
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "rgba(255, 255, 255, 0.30)",
                },
              },
              "& .MuiOutlinedInput-input::placeholder": {
                color: "text.secondary",
                opacity: 1,
              },
            }}
          />
        )}

        {tab === CHAT && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setSoundAnchorEl(e.currentTarget);
            }}
          >
            {soundEnabled ? (
              <SkuflixVolumeUpIcon color="disabled" fontSize="small" />
            ) : (
              <SkuflixVolumeOffIcon color="disabled" fontSize="small" />
            )}
          </IconButton>
        )}

        {tab === CHAT && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setOnlineListOpen(true);
            }}
          >
            <Badge badgeContent={onlineCount} color="primary" max={99}>
              <SkuflixPeopleIcon color="disabled" />
            </Badge>
          </IconButton>
        )}
      </Toolbar>

      {replyTo && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            px: 2,
            pb: 1,
            borderLeft: 2,
            borderColor: "primary.main",
            mx: 2,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" color="primary" fontWeight={500}>
              {replyTo.userDisplayName}
            </Typography>
            <Typography variant="caption" display="block" noWrap>
              {replyTo.message}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onCancelReply}>
            <SkuflixCrossIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      <ChatOnlineList
        open={onlineListOpen}
        onClose={() => setOnlineListOpen(false)}
        chatRoomId={chatRoomId}
        onMention={onMention}
        onlineMembers={onlineMembers}
      />

      {soundSettings && (
        <ChatSoundSettings
          settings={soundSettings}
          onUpdateSettings={onUpdateSoundSettings}
          anchorEl={soundAnchorEl}
          open={Boolean(soundAnchorEl)}
          onClose={() => setSoundAnchorEl(null)}
        />
      )}
    </StyledAppBar>
  );
});

ChatHeaderWithForm.propTypes = {
  tab: PropTypes.string.isRequired,
  enqueueSnackbar: PropTypes.func.isRequired,
  onOpenSignIn: PropTypes.func,
  rightholderId: PropTypes.string.isRequired,
  replyTo: PropTypes.shape({
    id: PropTypes.string.isRequired,
    userId: PropTypes.string,
    userDisplayName: PropTypes.string.isRequired,
    message: PropTypes.string.isRequired,
  }),
  onCancelReply: PropTypes.func,
  mention: PropTypes.shape({
    userId: PropTypes.string.isRequired,
    userDisplayName: PropTypes.string.isRequired,
  }),
  onClearMention: PropTypes.func,
  chatRoom: PropTypes.shape({
    id: PropTypes.string.isRequired,
    viewerIsAdmin: PropTypes.bool,
    viewerIsRightholder: PropTypes.bool,
    viewerBan: PropTypes.shape({
      id: PropTypes.string.isRequired,
      expiredAt: PropTypes.string.isRequired,
    }),
  }),
  viewer: PropTypes.shape({
    id: PropTypes.string.isRequired,
    fullName: PropTypes.string,
    photo: PropTypes.object,
    photoUrl: PropTypes.string,
    isAdmin: PropTypes.bool,
  }),
  onlineCount: PropTypes.number,
  onlineMembers: PropTypes.array,
  chatRoomId: PropTypes.string,
  onMention: PropTypes.func,
  soundSettings: PropTypes.object,
  onUpdateSoundSettings: PropTypes.func,
  onInputFocus: PropTypes.func,
  onInputBlur: PropTypes.func,
};

export default withSnackbar(ChatHeaderWithForm);
